import { configureStore, createAsyncThunk, createReducer } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { PlayModule, initPlayModule } from "./PlayModule";
import { applyMm2ModPack } from "./Mm2DiscPatch";
import { genieVisualPack } from "./Mm2DiagnosticPacks";

export type PlayUiState = {
  value: "idle" | "starting" | "initialized" | "loading-game" | "loaded" | "failed";
  message: string;
};

const initialState: PlayUiState = {
  value: "idle",
  message: "Tap Start Emulator to initialize Play! inside this browser.",
};

// Verified against the supported USA PACKAGE.BIN extracted from the user's raw
// MODE2/2352 image. Package entries 4 and 5 are the Natsume and Indi splash
// screens respectively. Both are 512x256 PSMT4 images followed by PSMCT32 CLUTs.
const NATIVE_BOOT_PIXEL_OFFSET = 0x901680;
const NATIVE_BOOT_CLUT_OFFSET = 0x911700;
const INDI_BOOT_PIXEL_OFFSET = 0x911bc0;
const INDI_BOOT_CLUT_OFFSET = 0x921c40;
const NATIVE_BOOT_EXPECTED_PIXELS = new Uint8Array(64).fill(0xee);
const NATIVE_BOOT_EXPECTED_CLUT = Uint8Array.from([
  28, 241, 18, 0, 5, 0, 0, 0, 70, 0, 58, 0, 92, 0, 86, 0,
  105, 0, 101, 0, 119, 0, 101, 0, 114, 0, 92, 0, 71, 0, 114, 0,
  122, 118, 180, 128, 139, 135, 181, 128, 167, 164, 186, 128,
  185, 183, 208, 128, 209, 207, 216, 128, 225, 223, 228, 128,
  242, 242, 242, 128, 255, 255, 255, 128,
]);
const INDI_BOOT_EXPECTED_PIXELS = Uint8Array.from([
  238, 239, 254, 254, 254, 254, 254, 254, 254, 238, 239, 238, 238, 238, 239, 254,
  254, 238, 254, 238, 238, 254, 238, 254, 238, 254, 238, 254, 238, 238, 238, 238,
  238, 239, 238, 238, 238, 239, 239, 238, 254, 238, 239, 238, 239, 254, 238, 238,
  238, 239, 238, 239, 238, 238, 239, 238, 239, 239, 254, 238, 254, 238, 254, 238,
]);
const INDI_BOOT_EXPECTED_CLUT = Uint8Array.from([
  28, 241, 18, 0, 5, 0, 0, 0, 70, 0, 58, 0, 92, 0, 86, 0,
  105, 0, 101, 0, 119, 0, 101, 0, 114, 0, 92, 0, 71, 0, 114, 0,
  210, 168, 82, 128, 146, 179, 201, 128, 175, 195, 208, 128,
  217, 196, 157, 128, 200, 209, 215, 128, 219, 217, 214, 128,
  227, 227, 227, 128, 237, 235, 234, 128,
]);

const BOOT_GLYPHS: Record<string, readonly string[]> = {
  " ": ["00000", "00000", "00000", "00000", "00000", "00000", "00000"],
  "!": ["00100", "00100", "00100", "00100", "00100", "00000", "00100"],
  ".": ["00000", "00000", "00000", "00000", "00000", "00110", "00110"],
  "/": ["00001", "00010", "00100", "01000", "10000", "00000", "00000"],
  A: ["01110", "10001", "10001", "11111", "10001", "10001", "10001"],
  B: ["11110", "10001", "10001", "11110", "10001", "10001", "11110"],
  C: ["01111", "10000", "10000", "10000", "10000", "10000", "01111"],
  E: ["11111", "10000", "10000", "11110", "10000", "10000", "11111"],
  G: ["01111", "10000", "10000", "10111", "10001", "10001", "01110"],
  H: ["10001", "10001", "10001", "11111", "10001", "10001", "10001"],
  I: ["11111", "00100", "00100", "00100", "00100", "00100", "11111"],
  J: ["00111", "00010", "00010", "00010", "10010", "10010", "01100"],
  K: ["10001", "10010", "10100", "11000", "10100", "10010", "10001"],
  L: ["10000", "10000", "10000", "10000", "10000", "10000", "11111"],
  M: ["10001", "11011", "10101", "10101", "10001", "10001", "10001"],
  N: ["10001", "11001", "10101", "10011", "10001", "10001", "10001"],
  O: ["01110", "10001", "10001", "10001", "10001", "10001", "01110"],
  P: ["11110", "10001", "10001", "11110", "10000", "10000", "10000"],
  T: ["11111", "00100", "00100", "00100", "00100", "00100", "00100"],
  U: ["10001", "10001", "10001", "10001", "10001", "10001", "01110"],
  W: ["10001", "10001", "10001", "10101", "10101", "10101", "01010"],
  X: ["10001", "10001", "01010", "00100", "01010", "10001", "10001"],
};

function makeNativeNetBootPixels(phase: "identity" | "jack-in" = "identity"): Uint8Array {
  const width = 512;
  const height = 256;
  const pixels = new Uint8Array(width * height);
  const put = (x: number, y: number, c: number) => {
    if (x >= 0 && y >= 0 && x < width && y < height) pixels[y * width + x] = c & 0x0f;
  };
  const rect = (x0: number, y0: number, x1: number, y1: number, c: number) => {
    for (let y = y0; y <= y1; y += 1) for (let x = x0; x <= x1; x += 1) put(x, y, c);
  };
  const line = (x0: number, y0: number, x1: number, y1: number, c: number) => {
    const dx = Math.abs(x1 - x0);
    const sx = x0 < x1 ? 1 : -1;
    const dy = -Math.abs(y1 - y0);
    const sy = y0 < y1 ? 1 : -1;
    let err = dx + dy;
    while (true) {
      put(x0, y0, c);
      if (x0 === x1 && y0 === y1) break;
      const e2 = err * 2;
      if (e2 >= dy) { err += dy; x0 += sx; }
      if (e2 <= dx) { err += dx; y0 += sy; }
    }
  };
  const text = (value: string, x: number, y: number, scale: number, c: number) => {
    let cursor = x;
    for (const ch of value) {
      const glyph = BOOT_GLYPHS[ch] || BOOT_GLYPHS[" "];
      for (let gy = 0; gy < 7; gy += 1) for (let gx = 0; gx < 5; gx += 1) {
        if (glyph[gy][gx] === "1") rect(cursor + gx * scale, y + gy * scale, cursor + (gx + 1) * scale - 1, y + (gy + 1) * scale - 1, c);
      }
      cursor += 6 * scale;
    }
  };

  pixels.fill(0);
  rect(16, 16, 495, 239, 1);
  rect(16, 16, 495, 18, 2);
  rect(16, 237, 495, 239, 5);
  rect(16, 16, 18, 239, 4);
  rect(493, 16, 495, 239, 6);
  line(28, 82, 484, 82, 4);
  line(28, 188, 484, 188, 5);
  for (let x = 32; x < 480; x += 32) {
    line(x, 28, x, 70, x % 64 === 0 ? 4 : 11);
    put(x, 28, 3);
    put(x, 70, 6);
  }
  for (let x = 30; x <= 482; x += 16) put(x, 188, x % 32 === 0 ? 3 : 6);

  // Enlarged GenieWoman lamp glyph. These chunkier dimensions are deliberate:
  // they survive MM2's PS2 filtering and 480i presentation far better than the
  // previous narrow emblem while staying inside the already-verified splash.
  rect(247, 30, 265, 34, 8);
  rect(252, 35, 260, 42, 7);
  for (let y = 18; y <= 29; y += 1) {
    const half = Math.max(1, Math.floor((31 - y) / 2));
    rect(256 - half, y, 256 + half, y, y < 24 ? 8 : 7);
  }
  for (let y = 42; y <= 61; y += 1) {
    const half = 10 + Math.floor((y - 42) / 2);
    rect(256 - half, y, 256 + half, y, 7);
  }
  rect(239, 60, 273, 65, 8);
  line(273, 47, 294, 42, 7);
  line(294, 42, 305, 47, 8);
  line(305, 47, 288, 52, 7);
  line(239, 47, 229, 42, 7);
  line(229, 42, 221, 48, 8);
  line(221, 48, 237, 58, 7);

  if (phase === "jack-in") {
    text("JACK IN!!", 82, 102, 6, 3);
    text("JACK IN!!", 84, 104, 6, 2);
    text("GENIEWOMAN.EXE", 130, 158, 3, 8);
  } else {
    text("GENIEWOMAN.EXE", 46, 102, 5, 3);
    text("GENIEWOMAN.EXE", 48, 104, 5, 2);
    text("LAMP HUB // NET", 124, 158, 3, 8);
  }
  for (const [x, y] of [[28, 28], [484, 28], [28, 227], [484, 227]] as const) {
    rect(x - 3, y - 3, x + 3, y + 3, 6);
    rect(x - 1, y - 1, x + 1, y + 1, 9);
  }

  const packed = new Uint8Array((width * height) / 2);
  for (let i = 0, p = 0; i < pixels.length; i += 2, p += 1) packed[p] = pixels[i] | (pixels[i + 1] << 4);
  return packed;
}

function makeNativeNetBootClut(): Uint8Array {
  const colors: readonly (readonly [number, number, number])[] = [
    [4, 6, 18], [9, 14, 35], [38, 224, 255], [170, 250, 255],
    [30, 92, 196], [102, 38, 181], [199, 91, 255], [242, 177, 54],
    [255, 225, 124], [247, 252, 255], [117, 133, 160], [15, 35, 69],
    [20, 63, 93], [84, 25, 119], [13, 153, 190], [255, 255, 255],
  ];
  const out = new Uint8Array(64);
  colors.forEach(([r, g, b], i) => {
    out[i * 4] = r;
    out[i * 4 + 1] = g;
    out[i * 4 + 2] = b;
    out[i * 4 + 3] = 0x80;
  });
  return out;
}

const genieFullVisualPack = {
  id: "mm2-usa-geniewoman-full-visual-v10",
  name: "GenieWoman MM2 Native Visual Conversion v10",
  description: "Verified native GenieWoman Face 1 plus a two-stage GenieWoman.EXE boot sequence with a larger 480i-readable lamp emblem: Lamp Hub identity followed by a native JACK IN!! handoff.",
  patches: [
    ...genieVisualPack.patches,
    {
      file: "PACKAGE.BIN" as const,
      offset: NATIVE_BOOT_PIXEL_OFFSET,
      bytes: makeNativeNetBootPixels("identity"),
      expected: NATIVE_BOOT_EXPECTED_PIXELS,
      label: "GenieWoman native NET identity splash pixels v2 (entry 4 / transfer 0)",
    },
    {
      file: "PACKAGE.BIN" as const,
      offset: NATIVE_BOOT_CLUT_OFFSET,
      bytes: makeNativeNetBootClut(),
      expected: NATIVE_BOOT_EXPECTED_CLUT,
      label: "GenieWoman native NET boot splash CLUT (entry 4 / transfer 1)",
    },
    {
      file: "PACKAGE.BIN" as const,
      offset: INDI_BOOT_PIXEL_OFFSET,
      bytes: makeNativeNetBootPixels("jack-in"),
      expected: INDI_BOOT_EXPECTED_PIXELS,
      label: "GenieWoman native JACK IN splash pixels v2 (entry 5 / transfer 0)",
    },
    {
      file: "PACKAGE.BIN" as const,
      offset: INDI_BOOT_CLUT_OFFSET,
      bytes: makeNativeNetBootClut(),
      expected: INDI_BOOT_EXPECTED_CLUT,
      label: "GenieWoman native NET boot splash CLUT (entry 5 / transfer 1)",
    },
  ],
};

export const init = createAsyncThunk<void>("init", async () => {
  await initPlayModule();
});

export const bootFile = createAsyncThunk<void, File>("bootFile", async (file: File, thunkAPI) => {
  if (PlayModule === null) {
    return thunkAPI.rejectWithValue("Play! is not initialized yet.") as never;
  }

  const fileName = file.name;
  const fileDotPos = fileName.lastIndexOf(".");
  if (fileDotPos === -1) {
    return thunkAPI.rejectWithValue("The selected file has no extension.") as never;
  }

  const fileExtension = fileName.substring(fileDotPos).toLowerCase();
  if (fileExtension === ".elf") {
    const url = URL.createObjectURL(file);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Unable to read ELF: HTTP ${response.status}`);
      const data = new Uint8Array(await response.arrayBuffer());
      const stream = PlayModule.FS.open(fileName, "w+");
      PlayModule.FS.write(stream, data, 0, data.length, 0);
      PlayModule.FS.close(stream);
      PlayModule.bootElf(fileName);
    } finally {
      URL.revokeObjectURL(url);
    }
  } else {
    const params = new URLSearchParams(window.location.search);
    const baseGameOnly = params.get("baseGame") === "1";
    const bootImage = baseGameOnly ? file : await applyMm2ModPack(file, genieFullVisualPack);
    PlayModule.discImageDevice.setFile(bootImage);
    PlayModule.bootDiscImage(fileName);
  }
});

const reducer = createReducer(initialState, (builder) =>
  builder
    .addCase(init.pending, (state) => {
      state.value = "starting";
      state.message = "Starting Play! WebAssembly…";
    })
    .addCase(init.fulfilled, (state) => {
      state.value = "initialized";
      state.message = "Play! initialized. Choose an MM2 image.";
    })
    .addCase(init.rejected, (state, action) => {
      state.value = "failed";
      state.message = action.error.message || "Play! initialization failed.";
    })
    .addCase(bootFile.pending, (state) => {
      state.value = "loading-game";
      state.message = "Applying GenieWoman visual conversion and handing the image to the emulated PS2…";
    })
    .addCase(bootFile.fulfilled, (state) => {
      state.value = "loaded";
      state.message = "GenieWoman visual conversion applied. Watch the emulator canvas for boot output.";
    })
    .addCase(bootFile.rejected, (state, action) => {
      state.value = "failed";
      state.message = String(action.payload || action.error.message || "Game load failed.");
    }),
);

export const store = configureStore({ reducer: { play: reducer } });
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
